import { useRef, useEffect } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import type { Location } from '@shared/types';

interface GoogleMapsAutocompleteProps {
  onPlaceSelect: (location: Location) => void;
  initialValue?: string;
  placeholder?: string;
}

const libraries: ('places')[] = ['places'];

/**
 * Never call useLoadScript with an empty key – the library loads js?key=demo-key and throws InvalidKey.
 */
export default function GoogleMapsAutocomplete({
  onPlaceSelect,
  initialValue = '',
  placeholder = 'Enter address',
}: GoogleMapsAutocompleteProps) {
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!googleMapsApiKey) {
    return (
      <PlainAddressInput
        placeholder={placeholder}
        initialValue={initialValue}
      />
    );
  }
  return (
    <GoogleMapsAutocompleteLoaded
      googleMapsApiKey={googleMapsApiKey}
      onPlaceSelect={onPlaceSelect}
      initialValue={initialValue}
      placeholder={placeholder}
    />
  );
}

/** No Maps script – manual address only */
function PlainAddressInput({
  placeholder,
  initialValue,
}: {
  placeholder: string;
  initialValue: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current && initialValue) inputRef.current.value = initialValue;
  }, [initialValue]);
  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder}
      className="input"
      defaultValue={initialValue}
    />
  );
}

function GoogleMapsAutocompleteLoaded({
  googleMapsApiKey,
  onPlaceSelect,
  initialValue,
  placeholder,
}: GoogleMapsAutocompleteProps & { googleMapsApiKey: string }) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry?.location) return;

    const location: Location = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      address: place.formatted_address || place.name || '',
      city: '',
      country: '',
    };

    place.address_components?.forEach((component) => {
      if (
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_2')
      ) {
        location.city = component.long_name;
      }
      if (component.types.includes('country')) location.country = component.long_name;
      if (component.types.includes('administrative_area_level_1'))
        location.region = component.long_name;
    });

    if (!location.city && place.formatted_address) {
      const parts = place.formatted_address.split(',');
      if (parts.length > 1) location.city = parts[parts.length - 2].trim();
    }

    if (inputRef.current) inputRef.current.value = location.address;
    onPlaceSelect(location);
  };

  useEffect(() => {
    if (inputRef.current && initialValue) inputRef.current.value = initialValue;
  }, [initialValue]);

  if (loadError) {
    return (
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="input"
        defaultValue={initialValue}
      />
    );
  }

  if (!isLoaded) {
    return (
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="input"
        defaultValue={initialValue}
        disabled
        aria-busy="true"
      />
    );
  }

  return (
    <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="input"
        defaultValue={initialValue}
      />
    </Autocomplete>
  );
}
